import type { ReactNode } from 'react';

import { Button } from '../../cdl/Button/Button';
import { translate } from '../../common/helpers/translate.helper';
import { useLocalStorageState } from '../../common/hooks/useLocalStorageState';
import { createOverlay } from '../../common/Overlay/Overlay';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { OverlaySidePanelLayout } from '../../common/Overlay/OverlaySidePanelLayout';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

interface DemandDefinitionFlowLayoutProps {
    title: string;
    onClose: () => void;
    isCloseDisabled?: boolean;
    isContentInteractionBlocked?: boolean;
    isBusy?: boolean;
    areActionsHidden?: boolean;
    contentOverlay?: ReactNode;
    actions: ReactNode;
    navigation: ReactNode;
    panelContent: ReactNode;
    panelOverlay: ReactNode;
    children: ReactNode;
}

export const DemandDefinitionFlowLayout = ({
    title,
    onClose,
    isCloseDisabled,
    isContentInteractionBlocked,
    isBusy,
    areActionsHidden,
    contentOverlay,
    actions,
    navigation,
    panelContent,
    panelOverlay,
    children,
}: DemandDefinitionFlowLayoutProps) => {
    const [showDetailsRaw, setShowDetails] = useLocalStorageState('closelink-demand-definition-details-visible', true);
    const showDetails = Boolean(showDetailsRaw);

    return createOverlay(
        <OverlayHeader
            title={title}
            onClose={onClose}
            isCloseDisabled={isCloseDisabled}
            maxContentWidth={theme.maxContentWidths.wide}
            trailingVisual={
                areActionsHidden ? null : (
                    <Box display="flex" alignItems="center" gap={8}>
                        <Button
                            type="button"
                            disabled={isContentInteractionBlocked}
                            emphasis="low"
                            aria-expanded={showDetails}
                            aria-controls="demand-definition-summary"
                            onClick={() => setShowDetails((previous) => !previous)}
                        >
                            {showDetails
                                ? translate('demandDefinition.hideDetails')
                                : translate('demandDefinition.showDetails')}
                        </Button>
                        {actions}
                    </Box>
                )
            }
        >
            <Box position="relative" height="100%" overflow={isContentInteractionBlocked ? 'hidden' : undefined}>
                <Box
                    inert={isContentInteractionBlocked || isBusy}
                    display="grid"
                    gridTemplateColumns="max-content minmax(0, 1fr)"
                    height="100%"
                    minHeight={0}
                >
                    <Box as="aside" width="max-content" padding={48}>
                        {navigation}
                    </Box>
                    <OverlaySidePanelLayout
                        open={showDetails}
                        panelId="demand-definition-summary"
                        panelContent={panelContent}
                        panelOverlay={panelOverlay}
                    >
                        {children}
                    </OverlaySidePanelLayout>
                </Box>
                {contentOverlay}
            </Box>
        </OverlayHeader>
    );
};
